import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { auth, googleProvider, db } from '../services/firebase';
import { onAuthStateChanged, signOut, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config';

const ADMIN_EMAILS = ['eduardo20032110@gmail.com']; 

export const AuthContext = createContext({ 
  user: null, 
  loading: true,
  logout: () => Promise.resolve(),
  loginWithGoogle: () => Promise.resolve()
});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isManualLogin = useRef(false);

  const sendApprovalRequestEmail = async (userData) => {
    const templateParams = {
      user_email: userData.email,
      user_name: userData.nombre,
    };
    try {
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );
    } catch (err) {
      console.warn("⚠️ Correo falló (no crítico):", err);
    }
  };

  // Función segura para obtener perfil
  const getUserProfile = async (uid) => {
     try {
       const userRef = doc(db, "users", uid);
       const userSnap = await getDoc(userRef);
       return userSnap.exists() ? userSnap.data() : null;
     } catch (error) {
       console.error("Error leyendo perfil de usuario:", error);
       // Si falla por permisos u otro error, retornamos null para manejarlo fuera
       return null;
     }
  };

  const loginWithGoogle = async () => {
    isManualLogin.current = true;

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const uid = result.user.uid;
      const email = result.user.email;
      const userRef = doc(db, "users", uid);
      
      // Intentamos obtener el perfil
      let userProfile = await getUserProfile(uid);

      // A) CASO ADMIN HARDCODED (Siempre entra)
      if (ADMIN_EMAILS.includes(email)) {
        if (!userProfile) {
          userProfile = {
            email: email,
            nombre: result.user.displayName || 'Admin',
            status: 'approved',
            role: 'admin',
            createdAt: new Date()
          };
          // Usamos setDoc con merge por seguridad
          await setDoc(userRef, userProfile, { merge: true });
        }
        // Si ya existía pero no era admin en DB, forzamos actualización (opcional)
        // Por ahora solo mezclamos el estado local
        setUser({ ...result.user, ...userProfile }); 
        return { user: result.user, status: 'APPROVED' };
      }

      // B) USUARIO EXISTENTE (Si userProfile no es null)
      if (userProfile) {
        if (userProfile.status === 'approved') {
          setUser({ ...result.user, ...userProfile });
          return { user: result.user, status: 'APPROVED' };
        } else {
          // Si está pendiente o rechazado
          await signOut(auth);
          return { user: null, status: 'PENDING' };
        }
      } 
      
      // C) USUARIO NUEVO (Si userProfile es null y no es admin)
      // Ojo: Si getUserProfile falló por error de permiso, podríamos caer aquí erróneamente.
      // Pero como acabamos de loguearnos, el permiso debería existir si el doc existe.
      // Si no existe, lo creamos.
      
      console.log("🆕 Creando usuario nuevo...");
      const newUser = {
        email: email,
        nombre: result.user.displayName || 'Sin nombre',
        status: 'pending',
        role: 'user',
        createdAt: new Date()
      };
      
      // Guardamos en DB
      await setDoc(userRef, newUser);
      
      // Enviamos correo
      sendApprovalRequestEmail(newUser);

      // Pequeña pausa para asegurar consistencia antes de cerrar sesión
      await new Promise(r => setTimeout(r, 500));

      await signOut(auth);
      return { user: null, status: 'NEW_CREATED' };

    } catch (error) {
      console.error("Error General Login:", error);
      await signOut(auth);
      throw error;
    } finally {
      isManualLogin.current = false;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (isManualLogin.current) return;

      if (currentUser) {
        const userProfile = await getUserProfile(currentUser.uid);
        
        // Verificamos si es Admin Hardcoded O si tiene perfil aprobado
        const isAdminHardcoded = ADMIN_EMAILS.includes(currentUser.email);
        const isApproved = userProfile && userProfile.status === 'approved';

        if (isAdminHardcoded || isApproved) {
           setUser({ ...currentUser, ...userProfile });
        } else {
           // Si no cumple requisitos, fuera
           await signOut(auth);
           setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark text-earthy-navy dark:text-white">Cargando...</div>;

  return (
    <AuthContext.Provider value={{ user, loading, logout, loginWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;